import React, { useEffect, useMemo, useState } from 'react';
import { Trip } from '../../redux/api/trips/trips.model';
import {
  useGetAllEquipmentByTokenQuery,
  useSearchEquipmentsQuery
} from '../../redux/api/equipment/equipmentApi';
import { TwSelectOption } from '../TwSelects/TwSelects';
import { TwEditEquipmentModal } from '../TwEditEquipmentModal/TwEditEquipmentModal';
import Box from '@mui/material/Box/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { EquipmentType } from '../../redux/api/equipment/equipment.model';
import { USER_ROLE } from '../../providers/User/User.model';
import '../TwLoadDetail/TwLoadDetail.scss';
import { useGetTripByIdQuery, useGetTripByTokenQuery } from '../../redux/api/trips/tripApi';

interface EquipmentFieldProps {
  driversEditEquipmentEnabled: boolean;
  equipmentType: EquipmentType;
  equipmentId?: string | null;
  userRole: USER_ROLE | undefined;
  segmentId: string;
  token?: string;
}

export const TwEquipmentField: React.FC<EquipmentFieldProps> = ({
  driversEditEquipmentEnabled,
  equipmentType,
  equipmentId,
  userRole,
  segmentId,
  token = ''
}): JSX.Element => {
  const [isEditEquipmentModalOpen, setIsEditEquipmentModalOpen] = useState<boolean>(false);

  const {
    data: {
      trip: tripFromToken = {} as Trip,
      isDriverAssignedToTrip = false,
      tenantSettings = {}
    } = {}
  } = useGetTripByTokenQuery(token, { skip: !token.length });
  const { data: equipmentFromToken = { equipments: [], total: 0 } } =
    useGetAllEquipmentByTokenQuery(token, { skip: !token.length });

  const { data: tripFromOrder = {} as Trip, refetch } = useGetTripByIdQuery(segmentId, {
    skip: token.length > 0
  });
  const { data: equipmentFromTrip = { equipments: [], total: 0 } } = useSearchEquipmentsQuery(
    {
      params: {
        deleted: false
      }
    },
    { skip: token.length > 0 }
  );

  const equipmentData = token.length > 0 ? equipmentFromToken : equipmentFromTrip;
  const trip = token.length > 0 ? tripFromToken : tripFromOrder;

  const equipmentList: TwSelectOption[] = equipmentData.equipments.reduce(
    (acc, curr) => {
      if (curr.type !== equipmentType) return acc;
      acc.push({ label: curr.name, value: curr.id });
      return acc;
    },
    [{ label: '-', value: '' }] as TwSelectOption[]
  );

  const equipmentTypeName = useMemo(() => {
    if (equipmentType === EquipmentType.truck) {
      return trip.truck?.name;
    }
    if (equipmentType === EquipmentType.trailer) {
      return trip.trailer?.name;
    }
  }, [
    equipmentType,
    tripFromOrder.trailer?.id,
    tripFromOrder.truck?.id,
    tripFromToken.trailer?.name,
    tripFromToken.truck?.name
  ]);

  function handleOpenModal(): void {
    setIsEditEquipmentModalOpen(true);
  }

  function handleClose(): void {
    setIsEditEquipmentModalOpen(false);
  }

  function isDriverValidated(): boolean {
    if (userRole && userRole !== 'DRIVER') return false;
    if (!token.length) return driversEditEquipmentEnabled;
    return isDriverAssignedToTrip && tenantSettings['allow-drivers-to-edit-truck-and-trailer'];
  }

  useEffect(() => {
    if (equipmentId && !token) {
      refetch();
    }
  }, [equipmentId, refetch]);

  return (
    <>
      <TwEditEquipmentModal
        open={isEditEquipmentModalOpen}
        idType={equipmentType.toLocaleLowerCase()}
        equipmentList={equipmentList}
        onClose={handleClose}
        segmentId={segmentId}
        token={token}
      />
      <Box component="div" className="load-detail__data-container__data__equipment__item">
        <Box component="span">{equipmentType[0] + equipmentType.slice(1).toLowerCase()} name</Box>
        <Box
          component="span"
          className="load-detail__data-container__data__equipment__name-wrapper"
        >
          {equipmentTypeName ? (
            <>
              {equipmentTypeName}
              {isDriverValidated() && (
                <Link
                  className="load-detail__data-container__data__equipment__link"
                  onClick={handleOpenModal}
                >
                  Edit
                </Link>
              )}
            </>
          ) : (
            <>
              {isDriverValidated() && equipmentList.length > 1 ? (
                <Link
                  className="load-detail__data-container__data__equipment__link"
                  onClick={handleOpenModal}
                >
                  Assign
                </Link>
              ) : (
                <Typography>-</Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};
